import React, { useEffect, useId, useMemo, useState } from "react";
import "./robot.css";

/*
  Stealth mini robot — layered SVG character animated with CSS (transforms + opacity only).
  Parts: float → body, legs, head (shell, faceplate, ears, eyes, mouth), left/right arm → hand.
  Each part has a "pose" layer (state-driven, eased with transitions) and a "motion" layer
  (small looping keyframes), so state changes blend instead of snapping.
  States: idle | listening | thinking | speaking | happy | greeting | walking | surprised | attentive
*/
export const ROBOT_STATES = ["idle", "listening", "thinking", "speaking", "happy", "greeting", "walking", "surprised", "attentive"];

// One-off arrival sequence: greet/explain → happy (thumbs-up) → idle. Timers are cleared on unmount.
export function useArrivalState(active, first) {
  const [phase, setPhase] = useState(active ? (first ? "greeting" : "speaking") : "idle");
  useEffect(() => {
    if (!active) return undefined;
    const t1 = setTimeout(() => setPhase("happy"), first ? 2600 : 3000);
    const t2 = setTimeout(() => setPhase("idle"), first ? 4000 : 4400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [active, first]);
  return phase;
}

function Hand({ id, side }) {
  const flip = side === "right" ? -1 : 1;
  return (
    <g className="rb-hand">
      {/* open hand */}
      <g className="rb-hand-open">
        <circle cx="0" cy="0" r="4.8" fill={`url(#${id}-dark)`} stroke="#9A9A9A" strokeWidth="0.6" />
        {[-2.6, -0.9, 0.9, 2.6].map((x, i) => (
          <rect key={i} x={x - 0.75} y={1.6} width="1.5" height={i === 0 || i === 3 ? 3.6 : 4.4} rx="0.75" fill="#141414" stroke="#7A7A7A" strokeWidth="0.35" />
        ))}
        <rect x={-4.6 * flip - 0.75} y={-1.8} width="1.5" height="3.4" rx="0.75" fill="#141414" stroke="#7A7A7A" strokeWidth="0.35" transform={`rotate(${35 * flip} ${-4.6 * flip} -0.1)`} />
      </g>
      {/* thumbs-up */}
      <g className="rb-hand-thumb">
        <rect x="-3.6" y="-2.4" width="7.2" height="6.4" rx="2.6" fill={`url(#${id}-dark)`} stroke="#9A9A9A" strokeWidth="0.6" />
        <rect x={flip > 0 ? 1.1 : -2.9} y="-7.2" width="1.8" height="5.6" rx="0.9" fill="#1A1A1A" stroke="#8A8A8A" strokeWidth="0.4" />
      </g>
    </g>
  );
}

function Arm({ id, side }) {
  const x = side === "left" ? 29 : 71;
  return (
    <g className={`rb-arm rb-arm-${side}`}>
      <g className="rb-arm-motion">
        <circle cx={x} cy="69" r="4.4" fill={`url(#${id}-dark)`} stroke="#6A6A6A" strokeWidth="0.4" />
        <rect x={x - 3.3} y="69" width="6.6" height="14" rx="3.3" fill={`url(#${id}-silver)`} stroke="#5A5A5A" strokeWidth="0.4" />
        <circle cx={x} cy="84" r="2.6" fill="#151515" />
        <rect x={x - 2.9} y="84" width="5.8" height="8" rx="2.6" fill={`url(#${id}-silver)`} stroke="#5A5A5A" strokeWidth="0.4" />
        <g transform={`translate(${x} 96)`}>
          <g className="rb-hand-motion"><Hand id={id} side={side} /></g>
        </g>
      </g>
    </g>
  );
}

function Eye({ cx, id }) {
  return (
    <g className="rb-eye" style={{ transformOrigin: `${cx}px 31px` }}>
      <ellipse cx={cx} cy="31" rx="9" ry="10.5" fill={`url(#${id}-eyeglow)`} className="rb-eye-glow" />
      <g className="rb-eye-open" style={{ transformOrigin: `${cx}px 31px` }}>
        <ellipse cx={cx} cy="31" rx="4.6" ry="6.4" fill={`url(#${id}-eye)`} />
        <ellipse cx={cx} cy="31" rx="4.6" ry="6.4" fill={`url(#${id}-dots)`} opacity="0.5" />
        <ellipse cx={cx - 1.4} cy="28.4" rx="1.2" ry="1.6" fill="#E8F6FF" opacity="0.75" />
      </g>
      <path className="rb-eye-happy" d={`M ${cx - 4.8} 32.5 Q ${cx} 25.5 ${cx + 4.8} 32.5`} fill="none" stroke="#38B6FF" strokeWidth="2.6" strokeLinecap="round" />
    </g>
  );
}

export function StealthRobot({ state = "idle", size = 52, className = "", testid = "stealth-robot", title }) {
  const raw = useId();
  const id = useMemo(() => `rb${raw.replace(/[^a-zA-Z0-9]/g, "")}`, [raw]);
  // Per-instance timing so several robots never blink or glance in sync (set once, no re-render loops).
  const timing = useMemo(() => ({
    "--rb-blink": `${(3.4 + Math.random() * 3.2).toFixed(2)}s`,
    "--rb-blink-delay": `${(-Math.random() * 4).toFixed(2)}s`,
    "--rb-look": `${(7 + Math.random() * 4).toFixed(2)}s`,
    "--rb-float-delay": `${(-Math.random() * 3).toFixed(2)}s`,
  }), []);
  const st = ROBOT_STATES.includes(state) ? state : "idle";
  return (
    <div className={`stealth-robot ${className}`} data-state={st} data-testid={testid} style={{ ...(typeof size === "number" ? { width: size, height: size * 1.2 } : { width: size, aspectRatio: "100 / 120" }), ...timing }}
      role={title ? "img" : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
      <svg viewBox="0 0 100 120" width="100%" height="100%" overflow="visible">
        <defs>
          <linearGradient id={`${id}-silver`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#FFFFFF" /><stop offset="0.35" stopColor="#D9D9D9" /><stop offset="0.7" stopColor="#9C9C9C" /><stop offset="1" stopColor="#5E5E5E" />
          </linearGradient>
          <linearGradient id={`${id}-dark`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3A3A3A" /><stop offset="1" stopColor="#050505" />
          </linearGradient>
          <radialGradient id={`${id}-face`} cx="0.35" cy="0.25" r="0.9">
            <stop offset="0" stopColor="#2B2B2B" /><stop offset="0.55" stopColor="#0B0B0B" /><stop offset="1" stopColor="#000000" />
          </radialGradient>
          <radialGradient id={`${id}-eye`} cx="0.5" cy="0.45" r="0.6">
            <stop offset="0" stopColor="#B8E8FF" /><stop offset="0.45" stopColor="#3DB8FF" /><stop offset="1" stopColor="#1477E0" />
          </radialGradient>
          <radialGradient id={`${id}-eyeglow`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="rgba(46,168,255,0.45)" /><stop offset="1" stopColor="rgba(46,168,255,0)" />
          </radialGradient>
          <pattern id={`${id}-dots`} width="1.4" height="1.4" patternUnits="userSpaceOnUse">
            <circle cx="0.7" cy="0.7" r="0.32" fill="#0A3D73" />
          </pattern>
        </defs>

        <ellipse cx="50" cy="116" rx="17" ry="2.6" className="rb-shadow" fill="rgba(255,255,255,0.08)" />
        <g className="rb-float">
          <g className="rb-body-motion">
            {/* legs (hover pods) */}
            <g className="rb-legs">
              <g className="rb-leg rb-leg-l">
                <path d="M38 95 h9 v7 a4.5 5.5 0 0 1 -9 0 z" fill={`url(#${id}-silver)`} stroke="#4A4A4A" strokeWidth="0.4" />
                <ellipse cx="42.5" cy="107.6" rx="3" ry="1.1" fill="#1A1A1A" />
              </g>
              <g className="rb-leg rb-leg-r">
                <path d="M53 95 h9 v7 a4.5 5.5 0 0 1 -9 0 z" fill={`url(#${id}-silver)`} stroke="#4A4A4A" strokeWidth="0.4" />
                <ellipse cx="57.5" cy="107.6" rx="3" ry="1.1" fill="#1A1A1A" />
              </g>
            </g>
            {/* torso */}
            <rect x="33" y="62" width="34" height="36" rx="14" fill={`url(#${id}-dark)`} />
            <rect x="36" y="63.5" width="28" height="32" rx="12" fill={`url(#${id}-silver)`} />
            <path d="M40 66 q10 -3 20 0" stroke="#FFFFFF" strokeOpacity="0.7" strokeWidth="1.2" fill="none" strokeLinecap="round" />
            <circle cx="50" cy="78" r="2.8" fill="#1A1A1A" stroke="#BDBDBD" strokeWidth="0.6" />
            <circle cx="50" cy="78" r="1.2" className="rb-core" fill="#F5F5F5" />
            {/* neck + head */}
            <rect x="45" y="55" width="10" height="8" rx="3" fill="#111" />
            <g className="rb-head">
              <g className="rb-head-motion">
                <rect x="14" y="4" width="72" height="54" rx="25" fill={`url(#${id}-silver)`} stroke="#4A4A4A" strokeWidth="0.5" />
                <path d="M24 9 q26 -9 52 0" stroke="#FFFFFF" strokeOpacity="0.85" strokeWidth="2" fill="none" strokeLinecap="round" />
                <rect x="10" y="22" width="7" height="16" rx="3.5" fill={`url(#${id}-dark)`} stroke="#8A8A8A" strokeWidth="0.6" />
                <rect x="83" y="22" width="7" height="16" rx="3.5" fill={`url(#${id}-dark)`} stroke="#8A8A8A" strokeWidth="0.6" />
                <rect x="20" y="11" width="60" height="41" rx="18" fill={`url(#${id}-face)`} stroke="#000" strokeWidth="0.8" />
                <path d="M26 16 q12 -4 24 -2" stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="2.4" fill="none" strokeLinecap="round" />
                <g className="rb-eyes-look">
                  <Eye cx={39} id={id} />
                  <Eye cx={61} id={id} />
                </g>
                <path className="rb-mouth" d="M44 43 Q50 47.5 56 43" fill="none" stroke="#D8D8D8" strokeWidth="1.6" strokeLinecap="round" />
                <path className="rb-mouth-flat" d="M46 44 L54 44" fill="none" stroke="#8C8C8C" strokeWidth="1.2" strokeLinecap="round" />
                <ellipse className="rb-mouth-o" cx="50" cy="44.5" rx="2.2" ry="2.8" fill="#050505" stroke="#D8D8D8" strokeWidth="1.2" />
              </g>
            </g>
            <Arm id={id} side="left" />
            <Arm id={id} side="right" />
          </g>
        </g>
      </svg>
    </div>
  );
}
